import * as React from 'react';
import { cn } from '../../lib/utils';
import { useChartColors } from './use-chart-colors';
import { ChartDataTable, summarizeSeries, type ChartTableSeries } from './chart-data-table';

export interface TimeSeriesPoint {
  t: number | string | Date;
  v: number;
}

export interface TimeSeriesSeries {
  id: string;
  label: string;
  data: TimeSeriesPoint[];
  /**
   * Explicit token-resolved color. Omit to take the next slot of the canonical
   * colorblind-safe data-viz ramp in order (via {@link useChartColors}).
   */
  color?: string;
  /** Show as filled area under the line. */
  fill?: boolean;
  /** Live = pulse on latest dot. */
  live?: boolean;
}

export interface TimeSeriesProps {
  series: TimeSeriesSeries[];
  height?: number;
  /** Optional setpoint dashed line. */
  setpoint?: number;
  /** Y-axis range override; auto if omitted. */
  yMin?: number;
  yMax?: number;
  /** Title rendered above chart. */
  title?: React.ReactNode;
  /** Eyebrow above title. */
  eyebrow?: React.ReactNode;
  className?: string;
}

/**
 * Tyndo TimeSeries — pure SVG, no Recharts dep.
 *
 * Multi-series line chart with optional setpoint, fill, live pulse. Designed
 * for ops-dense screens (Production, SCADA, Quality, Historian). Series without
 * an explicit `color` default to the canonical colorblind-safe data-viz ramp
 * via {@link useChartColors}, so light/dark/density mode all just work; an
 * explicit `color` per series still wins.
 */
export function TimeSeries({
  series,
  height = 200,
  setpoint,
  yMin,
  yMax,
  title,
  eyebrow,
  className,
}: TimeSeriesProps) {
  const palette = useChartColors(Math.max(series.length, 1));
  const colorFor = (s: TimeSeriesSeries, i: number) =>
    s.color ?? palette[i % palette.length];
  const padding = { top: 12, right: 12, bottom: 22, left: 36 };
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(640);

  React.useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(() => {
      if (containerRef.current) setWidth(containerRef.current.clientWidth);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const innerW = Math.max(0, width - padding.left - padding.right);
  const innerH = Math.max(0, height - padding.top - padding.bottom);

  const allPoints = series.flatMap((s) => s.data);
  const hasData = allPoints.length > 0;
  const xs = allPoints.map((p) => +new Date(p.t));
  const ys = allPoints.map((p) => p.v);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yLo = yMin ?? Math.min(...ys, setpoint ?? Infinity);
  const yHi = yMax ?? Math.max(...ys, setpoint ?? -Infinity);
  const xSpan = Math.max(1e-6, xMax - xMin);
  const ySpan = Math.max(1e-6, yHi - yLo);

  const xScale = (t: number | string | Date) =>
    padding.left + ((+new Date(t) - xMin) / xSpan) * innerW;
  const yScale = (v: number) =>
    padding.top + innerH - ((v - yLo) / ySpan) * innerH;

  const setpointY = setpoint !== undefined ? yScale(setpoint) : null;

  // 4 horizontal grid lines
  const gridYs = [0, 0.25, 0.5, 0.75, 1].map((p) => padding.top + p * innerH);

  // ── Textual equivalent (WCAG 1.1.1) ──────────────────────────────────
  // Built from the same `series` the SVG draws, so the table can never drift
  // from the picture. Timestamps are read out in the viewer's locale rather
  // than as epoch numbers.
  const tableCaption =
    typeof title === 'string' ? title : typeof eyebrow === 'string' ? eyebrow : 'Time series';
  const tableSeries: ChartTableSeries[] = React.useMemo(
    () =>
      series.map((s) => ({
        id: s.id,
        label: s.label,
        points: s.data.map((p) => ({
          label: new Date(p.t).toLocaleString(),
          value: Number.isFinite(p.v) ? p.v : null,
        })),
      })),
    [series],
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        'rounded-lg border border-border bg-surface-raised p-4',
        className
      )}
    >
      {(title || eyebrow) && (
        <div className="mb-3 flex items-center justify-between">
          <div>
            {eyebrow && <p className="t-eyebrow">{eyebrow}</p>}
            {title && (
              <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            )}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            {series.map((s, i) => (
              <span key={s.id} className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden
                  className="h-1.5 w-3 rounded-full"
                  style={{
                    backgroundColor: colorFor(s, i),
                  }}
                />
                {s.label}
              </span>
            ))}
          </div>
        </div>
      )}
      {!hasData ? (
        <div
          className="flex items-center justify-center text-xs text-muted-foreground"
          style={{ height }}
          role="img"
          aria-label="No data"
        >
          No data
        </div>
      ) : (
      <svg
        width={width}
        height={height}
        className="block"
        data-slot="chart-canvas"
        role="img"
        /* The graphic states what it shows. Without this it announced
         * nothing at all — an unlabelled <svg> is invisible to a reader. The
         * values themselves live in the table below. */
        aria-label={summarizeSeries(tableCaption, tableSeries)}
      >
        {/* Grid */}
        {gridYs.map((y, i) => (
          <line
            key={i}
            x1={padding.left}
            y1={y}
            x2={padding.left + innerW}
            y2={y}
            stroke="hsl(var(--border))"
            strokeWidth={1}
            strokeOpacity={i === 4 ? 1 : 0.3}
            strokeDasharray={i === 4 ? '0' : '2 3'}
          />
        ))}

        {/* Y axis labels (3 — top/mid/bottom) */}
        {[0, 0.5, 1].map((p, i) => {
          const y = padding.top + p * innerH;
          const v = yHi - p * (yHi - yLo);
          return (
            <text
              key={i}
              x={padding.left - 6}
              y={y + 3}
              textAnchor="end"
              fontSize={10}
              fill="hsl(var(--muted-foreground))"
              className="t-tabular"
            >
              {Math.round(v * 10) / 10}
            </text>
          );
        })}

        {/* Setpoint */}
        {setpointY !== null && (
          <>
            <line
              x1={padding.left}
              y1={setpointY}
              x2={padding.left + innerW}
              y2={setpointY}
              stroke="hsl(var(--alarm-major))"
              strokeWidth={1}
              strokeDasharray="3 3"
              strokeOpacity={0.7}
            />
            <text
              x={padding.left + innerW - 4}
              y={setpointY - 3}
              textAnchor="end"
              fontSize={10}
              fill="hsl(var(--alarm-major))"
              className="t-mono"
            >
              SP {setpoint}
            </text>
          </>
        )}

        {/* Series */}
        {series.map((s, i) => {
          if (s.data.length === 0) return null;
          const color = colorFor(s, i);
          const pts = s.data.map((p) => ({ x: xScale(p.t), y: yScale(p.v) }));
          const firstPt = pts[0];
          const last = pts[pts.length - 1];
          if (!firstPt || !last) return null;
          const linePath = `M ${firstPt.x},${firstPt.y} ${pts
            .slice(1)
            .map((p) => `L ${p.x},${p.y}`)
            .join(' ')}`;
          const areaPath = s.fill
            ? `${linePath} L ${last.x},${padding.top + innerH} L ${firstPt.x},${padding.top + innerH} Z`
            : null;
          return (
            <g key={s.id}>
              {areaPath && (
                <path d={areaPath} fill={color} fillOpacity={0.1} />
              )}
              <path
                d={linePath}
                fill="none"
                stroke={color}
                strokeWidth={1.6}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx={last.x} cy={last.y} r={2.5} fill={color} />
              {s.live && (
                <circle
                  cx={last.x}
                  cy={last.y}
                  r={5}
                  fill={color}
                  opacity={0.4}
                  className="tyndo-pulse"
                />
              )}
            </g>
          );
        })}
      </svg>
      )}
      <ChartDataTable caption={tableCaption} series={tableSeries} categoryLabel="Time" />
    </div>
  );
}
